import { createPortal } from 'react-dom';
import { configurationDifferences, resultFreshness, scoreBasisDifferences, scoreBasisMatches } from '../result-context';
import { ActiveFilters, facetChips } from '../ui/active-filters';
import { VirtualRows } from '../ui/virtual-rows';
import { AttributeTable } from '../ui/attribute-table';
import { useDeferredValue, useMemo } from 'react';
import { useViewState, ViewStateScope, useCompactScreen, RememberedScroll } from '../ui/session-state';
import { useEffect, useState } from "react";
import type { JSX, ReactNode } from "react";
import { Button, Checkbox, Menu, MenuDivider, MenuItem, MenuList, MenuPopover, MenuTrigger, PopoverTrigger, Tooltip } from "@fluentui/react-components";
import { AdaptivePopover as Popover, AdaptivePopoverSurface as PopoverSurface } from '../ui/adaptive-popover';
import { ArrowClockwiseRegular, ArrowDownloadRegular, ArrowRightRegular, ArrowSwapRegular, CheckmarkRegular, ChevronDownRegular, DeleteRegular, DismissRegular, DocumentArrowDownRegular, DocumentArrowUpRegular, DocumentTextRegular, EditRegular, FolderRegular, HistoryRegular, MoreHorizontalRegular } from "@fluentui/react-icons";
import { WHEEL_STAGE_MODEL_VERSION, resourceGroupForCharacter, wheelStageLabel } from "@minglun/domain";
import type { AttributeGain, Character, FinalAttributeResult, ResourceGroup, SolverResult, SolverHistoryEntry, SsrChoiceSuggestion, Wheel, Workbench } from "@minglun/domain";
import { compileSearchQuery, prepareSearchDocument } from "../search";
import { collectResultFacetValues, classifyResultComparison, compareResultAttributes, compareResultWheels, isActiveWheelResult, matchesResultComparisonChange, matchesResultMembers, resultComparisonChangeOptions, type ResultComparisonChange, type ResultComparisonVisibleChange, type ResultRankOrder } from "../result-filters";
import { comparesDifferentStudies, defaultComparisonIds, groupComparisonHistories, resultStudyName } from "../result-comparison";
import { updateComparisonExecutionState, updateWheelExecutionState } from "../solver-state";
import { activeStudy } from "../studies";
import { formatNumber, normalizeSsrChoiceSuggestions, categories, membersByCharacterOrder, resourceGroupOptions, derivedTagIds, tagNames, tagOptions, type PageProps, solverResultStatus, type FilterOption, useDialogFocus } from "../workbench-shared";
import { PresetNameDialog } from "../ui/overlays";
import { ResultTableMultiFilter, ResultPartnerFilter, ResultTableSort, ResultProgressHeader } from "../ui/result-filters";
import { TagPills, SearchBox, EmptyState, NoResults } from "../ui/controls";

const resultSearchDocuments = new WeakMap<Workbench, WeakMap<object, ReturnType<typeof prepareSearchDocument>>>();
function cachedResultDocument(workbench: Workbench, entry: object, create: () => ReturnType<typeof prepareSearchDocument>) {
  let cache = resultSearchDocuments.get(workbench);
  if (!cache) { cache = new WeakMap(); resultSearchDocuments.set(workbench, cache); }
  let document = cache.get(entry);
  if (!document) { document = create(); cache.set(entry, document); }
  return document;
}
const catalogIndexes = new WeakMap<Workbench, ReturnType<typeof buildCatalogIndex>>();
function buildCatalogIndex(workbench: Workbench) {
  return {
    characterById: new Map(workbench.characters.map(item => [item.id, item])),
    sourceById: new Map(workbench.sourceAttributes.map(item => [item.id, item])),
    categoryMap: categories(workbench),
    wheelById: new Map(workbench.fateWheels.map(item => [item.id, item])),
    tags: new Map(workbench.fateWheels.map(wheel => [wheel.id, derivedTagIds(workbench, wheel)])),
    labels: new Map(workbench.fateWheels.map(wheel => [wheel.id, tagNames(workbench, wheel)])),
    partnerGroupById: new Map(workbench.characters.map(character => [character.id, resourceGroupForCharacter(character)])),
  };
}
function catalogIndex(workbench: Workbench) {
  let index = catalogIndexes.get(workbench);
  if (!index) { index = buildCatalogIndex(workbench); catalogIndexes.set(workbench, index); }
  return index;
}

export function ResultsPage({ workbench, userPlan, setUserPlan, notify, confirmAction, requestedHistoryId, importResults, exportAllResults }: PageProps): JSX.Element {
  const allHistories = userPlan.solverHistory;
  const compact = useCompactScreen();
  const [sidebarSlot,setSidebarSlot] = useState<HTMLElement|null>(()=>document.getElementById('result-sidebar-slot'));
  useEffect(()=>setSidebarSlot(document.getElementById('result-sidebar-slot')),[]);
  const [recordsOpen, setRecordsOpen] = useState(false);
  const pickerRef = useDialogFocus(recordsOpen, () => setRecordsOpen(false));
  const [seenResult, setSeenResult] = useViewState<string | undefined>('seenResult', undefined);
  const [seenRequest, setSeenRequest] = useViewState<string | undefined>('seenRequest', undefined);
  const [resultStudyId, setResultStudyId] = useViewState('ResultsPage.resultStudyId', () => userPlan.activeStudyId ?? userPlan.studies?.[0]?.id);
  const study = userPlan.studies?.find((item) => item.id === resultStudyId) ?? activeStudy(userPlan);
  const currentStudyResult = study?.lastResult;
  const histories = allHistories.filter((entry) =>
    resultStudyId
      ? entry.studyId === resultStudyId || (!entry.studyId && (userPlan.studies?.length ?? 0) <= 1)
      : true,
  );
  const [historyId, setHistoryId] = useViewState('ResultsPage.historyId', () => histories[0]?.id ?? "current");
  const [viewMode, setViewMode] = useViewState<"single" | "compare">('ResultsPage.viewMode', "single");
  const [pendingComparisonId,setPendingComparisonId] = useState<string>();
  const [compareIds, setCompareIds] = useViewState<string[]>('ResultsPage.compareIds', []);
  const [studyMenuOpen, setStudyMenuOpen] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [selectedHistoryIds, setSelectedHistoryIds] = useState<Set<string>>(() => new Set());
  const [renameEntry, setRenameEntry] = useState<SolverHistoryEntry>();
  useEffect(() => {
    if (seenResult === currentStudyResult?.completedAt) return;
    setSeenResult(currentStudyResult?.completedAt);
    const latest = histories.find((entry) => entry.result.completedAt === currentStudyResult?.completedAt);
    if (latest) {
      setHistoryId(latest.id);
      setViewMode("single");
    }
  }, [currentStudyResult?.completedAt]);
  useEffect(() => {
    if (seenRequest === requestedHistoryId) return;
    setSeenRequest(requestedHistoryId);
    const requested = requestedHistoryId ? allHistories.find((entry) => entry.id === requestedHistoryId) : undefined;
    if (!requested) return;
    if (requested.studyId) setResultStudyId(requested.studyId);
    setHistoryId(requested.id);
    setViewMode("single");
  }, [requestedHistoryId, userPlan.solverHistory]);
  useEffect(() => {
    if (historyId !== "current" && !histories.some((entry) => entry.id === historyId))
      setHistoryId(histories[0]?.id ?? "current");
    setCompareIds((current) => current.filter((id) => allHistories.some((entry) => entry.id === id)));
  }, [historyId, userPlan.solverHistory, resultStudyId]);
  useEffect(() => {
    if (userPlan.studies?.some((item) => item.id === resultStudyId)) return;
    setResultStudyId(userPlan.activeStudyId ?? userPlan.studies?.[0]?.id);
  }, [userPlan.studies, userPlan.activeStudyId, resultStudyId]);
  const selectedHistoryEntry = histories.find((entry) => entry.id === historyId);
  const result = selectedHistoryEntry?.result ?? (historyId === "current" ? currentStudyResult : undefined);
  const resultConfiguration = selectedHistoryEntry?.configuration ?? userPlan.solver;
  const freshness = useMemo(()=>resultFreshness(selectedHistoryEntry, study, workbench),[selectedHistoryEntry,study,workbench]);
  const changedConfiguration=useMemo(()=>freshness==='changed' && selectedHistoryEntry && study ? configurationDifferences(selectedHistoryEntry,study,workbench) : [],[freshness,selectedHistoryEntry,study,workbench]);
  const unlimited = selectedHistoryEntry?.inventory?.some(item=>item.ignoreFragments) || Boolean(selectedHistoryEntry?.configuration?.ignoredFragmentGroups?.length);
  const isCurrentRule = result?.stageModelVersion === WHEEL_STAGE_MODEL_VERSION;
  const compareEntries = compareIds.map((id) => allHistories.find((entry) => entry.id === id)).filter((entry): entry is SolverHistoryEntry => Boolean(entry));
  const studyNameForHistory = (entry: SolverHistoryEntry): string => resultStudyName(entry, userPlan.studies);
  const compareGroups = groupComparisonHistories(allHistories, userPlan.studies, resultStudyId);
  const isCrossStudyComparison = comparesDifferentStudies(compareEntries);
  const exportHistory = async (entry: SolverHistoryEntry): Promise<void> => {
    try {
      const path = await window.minglun.exportResult(entry, workbench);
      if (path) notify("已导出结果");
    } catch (error) { notify(error instanceof Error ? error.message : "导出失败"); }
  };
  const renameHistory = (entryId: string, title: string): void => {
    setUserPlan((plan) => ({ ...plan, solverHistory: plan.solverHistory.map((entry) => entry.id === entryId ? { ...entry, title } : entry) }));
    setRenameEntry(undefined);
    notify(`已重命名为“${title}”`);
  };
  const deleteHistory = (entryId: string): void => {
    setUserPlan((plan) => ({ ...plan, solverHistory: plan.solverHistory.filter((entry) => entry.id !== entryId) }));
    notify("已移除记录");
  };
  const closeBatchMode = (): void => { setBatchMode(false); setSelectedHistoryIds(new Set()); };
  const deleteSelectedHistory = (): void => {
    const ids = new Set(selectedHistoryIds);
    setUserPlan((plan) => ({ ...plan, solverHistory: plan.solverHistory.filter((entry) => !ids.has(entry.id)) }));
    closeBatchMode();
    notify(`已移除 ${ids.size} 条记录`);
  };
  const requestBatchDelete = (): void => {
    if (!selectedHistoryIds.size) return;
    confirmAction({ title: `删除选中的 ${selectedHistoryIds.size} 条记录？`, message: "这些记录将在下次保存时从求解记录文件中删除。", confirmLabel: "批量删除", onConfirm: deleteSelectedHistory });
  };
  const switchResultStudy = (studyId: string): void => {
    const nextHistory = allHistories.find((entry) => entry.studyId === studyId);
    setResultStudyId(studyId);
    setHistoryId(nextHistory?.id ?? "current");
    setStudyMenuOpen(false);
    setBatchMode(false);
    setSelectedHistoryIds(new Set());
  };
  const enterCompare = (): void => {
    setBatchMode(false);
    setSelectedHistoryIds(new Set());
    setViewMode("compare");
    const viewedHistoryId = selectedHistoryEntry?.id
      ?? histories.find((entry) => entry.result.completedAt === result?.completedAt)?.id
      ?? histories[0]?.id;
    const defaults=defaultComparisonIds(allHistories, viewedHistoryId);
    setCompareIds(defaults);
    setPendingComparisonId(defaults.length<2 ? viewedHistoryId : undefined);
    if(defaults.length<2)setRecordsOpen(true);
  };
  const toggleCompare = (entryId: string): void => {
    if(pendingComparisonId) { if(entryId!==pendingComparisonId){setCompareIds([entryId,pendingComparisonId]);setPendingComparisonId(undefined);setRecordsOpen(false);} return; }
    setCompareIds((current) => {
    if (current.includes(entryId)) return current.filter((id) => id !== entryId);
    return current.length < 2 ? [...current, entryId] : [current[0]!, entryId];
  });
  };
  const setExecutionState = (wheelIds: string[], isApplied: boolean): void =>
    setUserPlan((plan) => updateWheelExecutionState(plan, historyId, wheelIds, isApplied));
  const historyRail = (<aside className="history-rail results-rail" ref={node => { pickerRef.current = node; }} role={recordsOpen ? "dialog" : undefined} aria-modal={recordsOpen ? true : undefined} aria-label="结果记录选择">
        {recordsOpen && <div className="records-sheet-header"><strong>{viewMode === 'compare' ? '选择对比记录' : '结果记录'}</strong><button type="button" aria-label="关闭结果选择" onClick={() => setRecordsOpen(false)}><DismissRegular /></button></div>}
        <div className="rail-heading">
          <div className="rail-title"><HistoryRegular /><span>{viewMode === "compare" ? "选择对比" : "结果"}</span><small>{viewMode === "compare" ? allHistories.length : histories.length}</small></div>
          <div className="rail-actions">
            {!batchMode && <Menu><MenuTrigger disableButtonEnhancement><button type="button" className="rail-icon" aria-label="结果导入导出" title="结果导入导出"><MoreHorizontalRegular /></button></MenuTrigger><MenuPopover><MenuList>
              <MenuItem icon={<DocumentArrowDownRegular />} onClick={() => void importResults()}>导入结果…</MenuItem>
              <MenuDivider />
              <MenuItem icon={<DocumentArrowUpRegular />} disabled={!allHistories.length} onClick={() => void exportAllResults()}>导出全部结果</MenuItem>
            </MenuList></MenuPopover></Menu>}
            {batchMode ? <>
              <button type="button" className="rail-icon" title="全选" aria-label="全选记录" onClick={() => setSelectedHistoryIds(new Set(histories.map((entry) => entry.id)))}><CheckmarkRegular /></button>
              <button type="button" className="rail-icon danger" disabled={!selectedHistoryIds.size} title="删除选中记录" aria-label="删除选中记录" onClick={requestBatchDelete}><DeleteRegular /></button>
              <button type="button" className="rail-icon" title="取消" aria-label="退出批量删除" onClick={closeBatchMode}><DismissRegular /></button>
            </> : viewMode === "single" ? <button type="button" className="rail-icon" disabled={!histories.length} aria-label="批量删除记录" title="批量删除记录" onClick={() => { setViewMode("single"); setBatchMode(true); }}><DeleteRegular /></button> : null}
          </div>
        </div>
        {viewMode === "single" ? <Menu open={studyMenuOpen} onOpenChange={(_event, data) => setStudyMenuOpen(data.open)}>
          <MenuTrigger disableButtonEnhancement>
            <button type="button" className={`results-scope ${studyMenuOpen ? "open" : ""}`} title="切换结果方案" aria-label={`当前方案：${study?.name ?? userPlan.name}，${histories.length} 条结果，点击切换`}>
              <FolderRegular />
              <span className="results-scope-copy"><small>当前方案 · {histories.length} 条结果</small><strong>{study?.name ?? userPlan.name}</strong></span>
              <span className="results-scope-chevron"><ChevronDownRegular /></span>
            </button>
          </MenuTrigger>
          <MenuPopover className="results-study-popover">
            <header className="results-study-popover-heading"><div><strong>切换方案</strong><small>结果按方案独立归档</small></div><span>{userPlan.studies?.length ?? 0}</span></header>
            <MenuList aria-label="切换结果方案">
            {(userPlan.studies ?? []).map((item) => {
              const entries = allHistories.filter((entry) => entry.studyId === item.id);
              const count = entries.length;
              const latest = entries[0];
              const active = item.id === resultStudyId;
              return <MenuItem key={item.id} icon={active ? <CheckmarkRegular /> : <DocumentTextRegular />} onClick={() => switchResultStudy(item.id)}>
                <span className="results-study-option">
                  <span className="results-study-option-name"><strong>{item.name}</strong>{active && <i>当前</i>}</span>
                  <span className="results-study-option-meta"><small>{latest ? `${solverResultStatus(latest.result)} · 评分 ${formatNumber(latest.result.objectiveScore ?? 0, 2)}` : "尚未运行"}</small><em>{count} 条</em></span>
                </span>
              </MenuItem>;
            })}
          </MenuList></MenuPopover>
        </Menu> : <div className="comparison-global-scope"><ArrowSwapRegular /><span><small>{pendingComparisonId ? "当前结果已设为 B，请选择 A" : "选择对比记录"}</small><strong>{compareIds.length < 2 ? `继续选择 ${compareIds.length ? "B" : "A"}` : "已就绪"}</strong></span><b>{compareIds.length}/2</b></div>}
        <RememberedScroll className="history-list" name={`history-list:${viewMode}:${resultStudyId}`}>
          {viewMode === "compare" ? compareGroups.map((group) => <section className="comparison-history-group" key={group.id}>
            <header><FolderRegular /><span>{group.name}</span><small>{group.entries.length}</small></header>
            <VirtualRows items={group.entries} estimate={52} itemKey={entry => entry.id}>{(entry) => {
              const compareIndex = pendingComparisonId === entry.id ? 1 : compareIds.indexOf(entry.id);
              return <button key={entry.id} className={`history-item compare-select ${compareIndex >= 0 ? "selected" : ""}`} aria-pressed={compareIndex >= 0} onClick={() => toggleCompare(entry.id)}><span>{entry.title}</span><small>{solverResultStatus(entry.result)} · 评分 {formatNumber(entry.result.objectiveScore ?? 0, 2)}</small>{compareIndex >= 0 && <b>{compareIndex === 0 ? "A" : "B"}</b>}</button>;
            }}</VirtualRows>
          </section>) : <>
          {!histories.length && currentStudyResult && <button className={`history-item ${historyId === "current" ? "active" : ""}`} onClick={() => { setViewMode("single"); setHistoryId("current"); setRecordsOpen(false); }}><span>当前结果</span><small>{solverResultStatus(currentStudyResult)}</small></button>}
          <VirtualRows items={histories} estimate={52} itemKey={entry => entry.id}>{(entry) => {
            if (batchMode) return <div className={`history-select-row ${selectedHistoryIds.has(entry.id) ? "selected" : ""}`} key={entry.id}><Checkbox checked={selectedHistoryIds.has(entry.id)} aria-label={`选择 ${entry.title}`} onChange={(_event, data) => setSelectedHistoryIds((current) => { const next = new Set(current); data.checked === true ? next.add(entry.id) : next.delete(entry.id); return next; })} /><button className={`history-item ${historyId === entry.id ? "active" : ""}`} onClick={() => { setHistoryId(entry.id); setRecordsOpen(false); }}><span>{entry.title}</span><small>{solverResultStatus(entry.result)}</small></button></div>;
            return <div className="history-record-row" key={entry.id}><Menu openOnContext><MenuTrigger disableButtonEnhancement><button className={`history-item ${historyId === entry.id ? "active" : ""}`} onClick={() => { setHistoryId(entry.id); setRecordsOpen(false); }}><span>{entry.title}</span><small>{solverResultStatus(entry.result)} · 评分 {formatNumber(entry.result.objectiveScore ?? 0, 2)}</small></button></MenuTrigger><MenuPopover><MenuList>
              <MenuItem icon={<EditRegular />} onClick={() => setRenameEntry(entry)}>重命名</MenuItem>
              <MenuItem icon={<ArrowDownloadRegular />} onClick={() => void exportHistory(entry)}>导出 .dat</MenuItem>
              <MenuItem icon={<DeleteRegular />} onClick={() => confirmAction({ title: "删除该求解记录？", message: "该记录将在下次保存时从文件中删除。", confirmLabel: "删除", onConfirm: () => deleteHistory(entry.id) })}>删除</MenuItem>
            </MenuList></MenuPopover></Menu><Menu positioning="below-end"><MenuTrigger disableButtonEnhancement><button className="history-record-menu" aria-label={`管理记录：${entry.title}`}><MoreHorizontalRegular /></button></MenuTrigger><MenuPopover><MenuList>
              <MenuItem icon={<EditRegular />} onClick={() => setRenameEntry(entry)}>重命名</MenuItem>
              <MenuItem icon={<ArrowDownloadRegular />} onClick={() => void exportHistory(entry)}>导出结果</MenuItem>
              <MenuItem icon={<DeleteRegular />} onClick={() => confirmAction({ title: "删除该求解记录？", message: "删除后无法恢复。", confirmLabel: "删除", onConfirm: () => deleteHistory(entry.id) })}>删除</MenuItem>
            </MenuList></MenuPopover></Menu></div>;
          }}</VirtualRows></>}
        </RememberedScroll>
      </aside>);
  return (
    <div className={`results-layout ${recordsOpen ? 'records-open' : ''}`}>
      {recordsOpen && <button type="button" className="records-backdrop" aria-label="关闭结果选择" onClick={() => setRecordsOpen(false)} />}
      {compact || recordsOpen ? historyRail : sidebarSlot ? createPortal(historyRail, sidebarSlot) : null}
      <main className="results-main">
        <div className="results-record-toolbar"><button type="button" onClick={() => setRecordsOpen(open => !open)} aria-label={viewMode === 'compare' ? '选择 A/B 记录' : '选择结果记录'} aria-expanded={recordsOpen}><HistoryRegular /><span>{viewMode === 'compare' ? '选择 A/B 记录' : selectedHistoryEntry?.title ?? '选择结果记录'}</span><ChevronDownRegular /></button></div>
        <header className="results-header">
          {viewMode === "single" ? <>
            <div className="results-title"><small>{study?.name ?? userPlan.name} · 计算结果</small><div><h1>{selectedHistoryEntry?.title ?? "当前结果"}</h1>{selectedHistoryEntry && <Tooltip content="重命名记录" relationship="label"><button type="button" aria-label="重命名当前结果记录" onClick={() => setRenameEntry(selectedHistoryEntry)}><EditRegular /></button></Tooltip>}</div></div>
            <div className="results-header-actions"><div className="results-summary"><span className={`status-pill ${result?.status ?? "not_run"}`}>{solverResultStatus(result)}</span><span><small title="评分取决于优化偏好，不是游戏战力">评分 ⓘ</small><strong>{result?.objectiveScore === undefined ? "—" : formatNumber(result.objectiveScore, 2)}</strong></span><span><small>耗时</small><strong>{result ? `${formatNumber(result.elapsedMilliseconds / 1000, 2)} 秒` : "—"}</strong></span></div><button type="button" className="results-compare-launch" disabled={!allHistories.length} onClick={enterCompare}><ArrowSwapRegular />与上次对比</button></div>
          </> : <>
            <div className="comparison-results-title"><small>{isCrossStudyComparison ? "跨方案结果对比" : "方案内结果对比"}{isCrossStudyComparison ? " · 评分按各自方案口径" : ""}</small>{compareEntries.length === 2 ? <div className="comparison-pair"><span><b>A</b><i>{studyNameForHistory(compareEntries[0]!)}</i><strong>{compareEntries[0]!.title}</strong></span><ArrowRightRegular /><span><b>B</b><i>{studyNameForHistory(compareEntries[1]!)}</i><strong>{compareEntries[1]!.title}</strong></span></div> : <h1>选择两条结果进行对比</h1>}</div>
            <div className="compare-header-actions"><button type="button" onClick={() => { setRecordsOpen(true); document.querySelector<HTMLElement>('.results-rail .history-item')?.focus(); }}><HistoryRegular />选择记录</button><button type="button" disabled={compareIds.length !== 2} onClick={() => setCompareIds(([left, right]) => left && right ? [right, left] : compareIds)}><ArrowClockwiseRegular />交换 A/B</button><button type="button" onClick={() => setViewMode("single")}><DismissRegular />退出对比</button></div>
          </>}
        </header>
        {viewMode === 'single' && result && result.status !== 'not_run' && <div className="result-context-notice">
          <span>{result.status === 'failed' ? '计算失败，无可用结果' : result.status === 'cancelled' ? '计算已取消，无可保留结果' : result.isProvenOptimal ? '已找到当前配置下的最优方案' : result.terminationReason === 'user_stop' ? '已停止并保留结果，未证明最优' : '已有可用结果，未证明最优'}</span>
          {unlimited && <span className="config-warning">含不限制碎片的规划条件，可能超过当前资源</span>}
          {freshness === 'changed' && <details className="configuration-differences"><summary>配置已修改 · 查看 {changedConfiguration.length} 项差异</summary><ul>{changedConfiguration.map((text,index)=><li key={index}>{text}</li>)}</ul></details>}
          {freshness === 'unknown' && <span>旧记录信息不足，无法确认是否与当前配置一致</span>}
        </div>}
        <div className="results-region" data-scroll-id="results-region">
          {viewMode === "compare" ? (
            compareEntries.length === 2 ? <ViewStateScope scope={`compare:${compareIds.join(':')}`}><ResultComparisonView key={compareIds.join(':')} baseline={compareEntries[0]!} comparison={compareEntries[1]!} workbench={workbench} onSetExecutionState={(wheelIds, isApplied) => setUserPlan((plan) => updateComparisonExecutionState(plan, compareEntries[1]!.id, compareEntries[0]!.result.completedAt, wheelIds, isApplied))} /></ViewStateScope> : <EmptyState label="请选择两条结果记录" detail="记录按方案分组，A 为基准，B 为对比结果" />
          ) : !result || result.status === "not_run" ? <EmptyState label="暂无求解结果" detail="完成一次计算后，结果会自动出现在这里" /> : isCurrentRule ? <ViewStateScope scope={`result:${historyId}`}><ResultView key={historyId} fragmentEnabled={resultConfiguration.fragmentAnalysisEnabled} result={result} workbench={workbench} ssrWheelChoices={resultConfiguration.ssrWheelChoices} ssrChoiceSuggestions={normalizeSsrChoiceSuggestions(result, workbench, resultConfiguration.ssrWheelChoices)} onSetExecutionState={setExecutionState} /></ViewStateScope> : <EmptyState label="该记录需按当前计算规则重新计算" />}
        </div>
      </main>
      {renameEntry && <PresetNameDialog title="重命名结果记录" fieldLabel="记录名称" initialName={renameEntry.title} confirmLabel="确认重命名" existingNames={userPlan.solverHistory.filter((entry) => entry.id !== renameEntry.id).map((entry) => entry.title)} onCancel={() => setRenameEntry(undefined)} onConfirm={(title) => renameHistory(renameEntry.id, title)} />}
    </div>
  );
}

export interface ResultComparisonWheelItem {
  wheel: Wheel;
  entry: SolverResult["fateWheels"][number];
  baseline: SolverResult["fateWheels"][number] | undefined;
  comparison: SolverResult["fateWheels"][number] | undefined;
  change: ResultComparisonChange;
}

export function ResultComparisonView({
  baseline,
  comparison,
  workbench,
  onSetExecutionState,
}: {
  baseline: SolverHistoryEntry;
  comparison: SolverHistoryEntry;
  workbench: Workbench;
  onSetExecutionState(wheelIds: string[], isApplied: boolean): void;
}): JSX.Element {
  const [comparisonSection, setComparisonSection] = useViewState<"wheels" | "attributes">('ResultComparisonView.comparisonSection', "wheels");
  const [query, setQuery] = useViewState('ResultComparisonView.query', "");
  const deferredQuery = useDeferredValue(query);
  const [categoryIds, setCategoryIds] = useViewState<string[]>('ResultComparisonView.categoryIds', []);
  const [tagIds, setTagIds] = useViewState<string[]>('ResultComparisonView.tagIds', []);
  const [partnerIds, setPartnerIds] = useViewState<string[]>('ResultComparisonView.partnerIds', []);
  const [partnerGroups, setPartnerGroups] = useViewState<string[]>('ResultComparisonView.partnerGroups', []);
  const [rankOrder, setRankOrder] = useViewState<ResultRankOrder>('ResultComparisonView.rankOrder', "默认");
  const [onlyChanged,setOnlyChanged] = useViewState('ResultComparisonView.onlyChanged',true);
  const [changeFilters, setChangeFilters] = useViewState<ResultComparisonVisibleChange[]>('ResultComparisonView.changeFilters', []);
  const [progress, setProgress] = useViewState('ResultComparisonView.progress', "全部");
  const { characterById, sourceById, categoryMap, wheelById, tags, labels: catalogLabels, partnerGroupById } = catalogIndex(workbench);
  const baselineById = new Map(baseline.result.fateWheels.filter(isActiveWheelResult).map((entry) => [entry.fateWheelId, entry]));
  const comparisonById = new Map(comparison.result.fateWheels.filter(isActiveWheelResult).map((entry) => [entry.fateWheelId, entry]));
  const appliedDifferenceIds = new Set(
    comparison.comparisonExecutions?.find(
      (item) => item.baselineCompletedAt === baseline.result.completedAt,
    )?.appliedFateWheelIds ?? [],
  );
  const allWheels = useMemo((): ResultComparisonWheelItem[] => [...new Set([...baselineById.keys(), ...comparisonById.keys()])]
    .map((id) => {
      const wheel = wheelById.get(id);
      const left = baselineById.get(id);
      const right = comparisonById.get(id);
      if (!wheel || (!left && !right)) return undefined;
      const change = classifyResultComparison(left, right);
      return { wheel, baseline: left, comparison: right, entry: right ?? left!, change };
    })
    .filter((item): item is ResultComparisonWheelItem => Boolean(item)), [baseline.result, comparison.result, wheelById]);
  type OmittedResultFacet = "category" | "tag" | "partner" | "quality";
  const searchDocuments = useMemo(() => new Map(allWheels.map(item => {
    const categoryName = categoryMap.get(item.wheel.wheelCategoryId)?.name ?? "";
    const members = item.wheel.members.map(member => characterById.get(member.characterId)).filter((character): character is Character => Boolean(character));
    const labels = catalogLabels.get(item.wheel.id) ?? [];
    const executionStatus = item.change === "不变" ? "无需更改" : appliedDifferenceIds.has(item.wheel.id) ? "已完成" : "未完成";
    return [item.wheel.id, prepareSearchDocument({
        text: [item.wheel.name, categoryName, ...labels, ...members.map((member) => member.name), item.change, executionStatus],
        fields: {
          name: item.wheel.name,
          category: categoryName,
          tag: labels,
          partner: members.map((member) => member.name),
          change: item.change,
          progress: executionStatus,
          baseline: item.baseline ? `${item.baseline.rank}阶 ${item.baseline.star}星` : "无",
          comparison: item.comparison ? `${item.comparison.rank}阶 ${item.comparison.star}星` : "无",
        },
        aliases: { 名称: "name", 关系命轮: "name", 类别: "category", 标签: "tag", 伙伴: "partner", 变化: "change", 进度: "progress", 状态: "progress", 基准: "baseline", 对比: "comparison" },
    })];
  })), [allWheels, workbench, comparison.comparisonExecutions, baseline.result.completedAt]);
  const searchMatches = useMemo(() => {
    const matches = compileSearchQuery(deferredQuery);
    return new Set(allWheels.filter(item => matches(searchDocuments.get(item.wheel.id)!)).map(item => item.wheel.id));
  }, [allWheels, searchDocuments, deferredQuery]);
  const matchesFilters = (item: ResultComparisonWheelItem, omittedFacet?: OmittedResultFacet): boolean => {
    const executionStatus = item.change === "不变" ? "无需更改" : appliedDifferenceIds.has(item.wheel.id) ? "已完成" : "未完成";
    return searchMatches.has(item.wheel.id)
      && (!onlyChanged || item.change !== "不变")
      && matchesResultComparisonChange(item.change, changeFilters)
      && (omittedFacet === "category" || !categoryIds.length || categoryIds.includes(item.wheel.wheelCategoryId))
      && (omittedFacet === "tag" || !tagIds.length || (tags.get(item.wheel.id) ?? []).some(tagId => tagIds.includes(tagId)))
      && (progress === "全部" || executionStatus === progress)
      && matchesResultMembers(item.wheel, characterById, omittedFacet === "partner" ? [] : partnerIds, omittedFacet === "quality" ? [] : partnerGroups);
  };
  const facetsFor = (omitted: OmittedResultFacet) => collectResultFacetValues(allWheels.filter((item) => matchesFilters(item, omitted)), characterById, (wheel) => tags.get(wheel.id) ?? []);
  const categoryFacets = facetsFor("category");
  const tagFacets = facetsFor("tag");
  const partnerFacets = facetsFor("partner");
  const qualityFacets = facetsFor("quality");
  const categoryOptions: FilterOption[] = workbench.wheelCategories.filter((category) => categoryFacets.categoryIds.has(category.id)).map((category) => ({ value: category.id, label: category.name }));
  const resultTagOptions = tagOptions(workbench).slice(1).filter((option) => tagFacets.tagIds.has(option.value));
  const partnerOptions: FilterOption[] = workbench.characters.filter((character) => partnerFacets.partnerIds.has(character.id)).sort((left, right) => left.order - right.order).map((character) => ({ value: character.id, label: character.name }));
  const qualityOptions = resourceGroupOptions.slice(1).filter((option) => qualityFacets.partnerGroups.has(option.value as ResourceGroup));
  const candidateKey = [categoryOptions, resultTagOptions, partnerOptions, qualityOptions].map((options) => options.map((option) => option.value).join("\u0000")).join("\u0001");
  useEffect(() => {
    const retain = (current: string[], available: Set<string>): string[] => {
      const next = current.filter((value) => available.has(value));
      return next.length === current.length ? current : next;
    };
    setCategoryIds((current) => retain(current, categoryFacets.categoryIds));
    setTagIds((current) => retain(current, tagFacets.tagIds));
    setPartnerIds((current) => retain(current, partnerFacets.partnerIds));
    setPartnerGroups((current) => retain(current, qualityFacets.partnerGroups));
  }, [candidateKey]);
  const visibleWheels = allWheels.filter((item) => matchesFilters(item)).sort((left, right) => compareResultWheels(left, right, rankOrder));
  const attributes = compareResultAttributes(baseline.result.finalAttributes, comparison.result.finalAttributes)
    .sort((left, right) => (sourceById.get(left.sourceAttributeId)?.order ?? 0) - (sourceById.get(right.sourceAttributeId)?.order ?? 0) || left.valueKind.localeCompare(right.valueKind));
  const attributesByGroup = new Map<string, typeof attributes>();
  for (const item of attributes.filter(item=>!onlyChanged || Math.abs(item.delta)>1e-9)) {
    const group = sourceById.get(item.sourceAttributeId)?.group ?? "其他属性";
    attributesByGroup.set(group, [...(attributesByGroup.get(group) ?? []), item]);
  }
  const attributeGroups = [...attributesByGroup.entries()].sort(
    ([, left], [, right]) =>
      (sourceById.get(left[0]?.sourceAttributeId ?? "")?.order ?? 0) -
      (sourceById.get(right[0]?.sourceAttributeId ?? "")?.order ?? 0),
  );
  const changedAttributes = attributes.filter((item) => Math.abs(item.delta) > 1e-9).length;
  const changedWheels = allWheels.filter((item) => item.change !== "不变").length;
  const scoreBefore = baseline.result.objectiveScore ?? 0;
  const scoreAfter = comparison.result.objectiveScore ?? 0;
  const scoreDelta = scoreAfter - scoreBefore;
  const comparable = scoreBasisMatches(baseline,comparison);
  const basisDifferences=scoreBasisDifferences(baseline,comparison);
  const differenceTasks = allWheels.filter((item) => item.change !== "不变");
  const appliedWheelCount = differenceTasks.filter((item) => appliedDifferenceIds.has(item.wheel.id)).length;
  const executableWheels = visibleWheels.filter((item) => item.change !== "不变");
  const visibleExecutableIds = executableWheels.map((item) => item.wheel.id);
  const allVisibleApplied = executableWheels.length > 0 && executableWheels.every((item) => appliedDifferenceIds.has(item.wheel.id));
  const someVisibleApplied = executableWheels.some((item) => appliedDifferenceIds.has(item.wheel.id));
  const bulkChecked: boolean | "mixed" = allVisibleApplied ? true : someVisibleApplied ? "mixed" : false;
  const resetFilters = (): void => { setQuery(""); setCategoryIds([]); setTagIds([]); setPartnerIds([]); setPartnerGroups([]); setRankOrder("默认"); setChangeFilters([]); setProgress("全部"); };
  const activeFilterGroups = [Boolean(query), categoryIds.length > 0, tagIds.length > 0, partnerIds.length > 0, partnerGroups.length > 0, rankOrder !== "默认", changeFilters.length > 0, progress !== "全部"].filter(Boolean).length;
  const stage = (entry: SolverResult["fateWheels"][number] | undefined): string => entry ? wheelStageLabel(entry.rank, entry.star, workbench.globalSettings.maxRank) : "—";
  return (
    <div className="result-comparison-view">
      <section className="comparison-overview" aria-label="对比摘要">
        <div><small>评分 A</small><strong>{formatNumber(scoreBefore, 2)}</strong></div>
        <span className="comparison-arrow"><ArrowRightRegular /></span>
        <div><small>评分 B</small><strong>{formatNumber(scoreAfter, 2)}</strong></div>
        {comparable ? <div className={`comparison-delta ${scoreDelta > 0 ? "positive" : scoreDelta < 0 ? "negative" : ""}`}><small>评分变化</small><strong>{scoreDelta > 0 ? "+" : ""}{formatNumber(scoreDelta, 2)}</strong></div> : <details className="configuration-differences"><summary>评分口径不同或无法确认 · 查看原因</summary><ul>{basisDifferences.map(reason=><li key={reason}>{reason}</li>)}</ul></details>}
        <div><small>方案变化</small><strong>{changedWheels}</strong></div>
        <div><small>属性变化</small><strong>{changedAttributes}</strong></div>
      </section>
      <div className="change-shortcuts" aria-label="变化分类快捷筛选">{resultComparisonChangeOptions.map(change=><button type="button" key={change} aria-pressed={changeFilters.includes(change)} onClick={()=>{setComparisonSection('wheels');setChangeFilters(old=>old.includes(change)?old.filter(x=>x!==change):[...old,change]);}}>{change}<b>{allWheels.filter(item=>matchesResultComparisonChange(item.change,[change])).length}</b></button>)}</div>
      <Checkbox className="only-changed" label="只看变化" checked={onlyChanged} onChange={(_,data)=>setOnlyChanged(data.checked===true)}/>
      <nav className="result-view-switcher" aria-label="对比视图">
        <button type="button" className={comparisonSection === "wheels" ? "active" : ""} aria-current={comparisonSection === "wheels" ? "page" : undefined} onClick={() => setComparisonSection("wheels")}><span>关系变化</span><small>{changedWheels}</small></button>
        <button type="button" className={comparisonSection === "attributes" ? "active" : ""} aria-current={comparisonSection === "attributes" ? "page" : undefined} onClick={() => setComparisonSection("attributes")}><span>属性变化</span><small>{changedAttributes}</small></button>
      </nav>
      {comparisonSection === "wheels" && <section className="comparison-section result-section-panel" aria-labelledby="wheel-comparison-title">
        <header className="comparison-section-heading"><div><h2 id="wheel-comparison-title">关系变化</h2><small>{visibleWheels.length} / {allWheels.length} 项</small></div><span>A 为基准，B 为对比结果</span></header>
        <div className="result-toolbar comparison-toolbar">
          <SearchBox value={query} onChange={setQuery} placeholder="筛选方案变化" resultCount={visibleWheels.length} totalCount={allWheels.length} />
          <span className="result-execution-summary" aria-label={`方案差异任务已完成 ${appliedWheelCount}，共 ${differenceTasks.length} 项`}>
            <CheckmarkRegular /><span>已完成</span><b>{appliedWheelCount}</b><i>/</i><span>{differenceTasks.length}</span>
          </span>
          <div className="result-toolbar-actions">{activeFilterGroups > 0 && <Button appearance="subtle" size="small" icon={<DismissRegular />} onClick={resetFilters}>清除筛选</Button>}</div>
        </div>
        <ActiveFilters clear={resetFilters} items={[
          ...facetChips('类别',categoryIds,categoryOptions,setCategoryIds),...facetChips('标签',tagIds,resultTagOptions,setTagIds),...facetChips('伙伴',partnerIds,partnerOptions,setPartnerIds),...facetChips('品质',partnerGroups,qualityOptions,setPartnerGroups),
          ...changeFilters.map(value=>({key:`change:${value}`,label:`变化：${value}`,remove:()=>setChangeFilters(old=>old.filter(x=>x!==value))})),
          ...(query?[{key:'search',label:`搜索：${query}`,remove:()=>setQuery('')}]:[]),...(progress!=='全部'?[{key:'progress',label:`进度：${progress}`,remove:()=>setProgress('全部')}]:[]),...(rankOrder!=='默认'?[{key:'sort',label:`排序：${rankOrder}`,remove:()=>setRankOrder('默认')}]:[])
        ]}/>
        <details className="execution-help"><summary>执行进度 · 当前筛选 {executableWheels.length} 项可标记</summary><p>勾选只记录游戏内调整是否完成，不会扣除库存，也不会代你操作游戏。表头勾选仅影响当前筛选结果。</p></details>
        <div className="data-table result-table comparison-wheel-table">
          <div className="table-row table-head">
            <ResultProgressHeader checked={bulkChecked} disabled={!executableWheels.length} filter={progress} onToggleAll={() => onSetExecutionState(visibleExecutableIds, !allVisibleApplied)} onFilterChange={setProgress} />
            <ResultTableMultiFilter label="变化" options={resultComparisonChangeOptions.map((value) => ({ value, label: value }))} selectedValues={changeFilters} onChange={(values) => setChangeFilters(values as ResultComparisonVisibleChange[])} />
            <span>关系命轮</span>
            <ResultTableMultiFilter label="类别" options={categoryOptions} selectedValues={categoryIds} onChange={setCategoryIds} />
            <div className="comparison-tags-column"><ResultTableMultiFilter label="标签" options={resultTagOptions} selectedValues={tagIds} onChange={setTagIds} /></div>
            <ResultPartnerFilter partnerOptions={partnerOptions} qualityOptions={qualityOptions} partnerGroupById={partnerGroupById} selectedPartnerIds={partnerIds} selectedQualityIds={partnerGroups} onPartnerChange={setPartnerIds} onQualityChange={setPartnerGroups} />
            <span>A 阶星</span>
            <ResultTableSort label="B 阶星" value={rankOrder} onChange={setRankOrder} />
          </div>
          <VirtualRows estimate={38} items={visibleWheels} itemKey={item => item.wheel.id}>{(item) => <ResultRow id={item.wheel.id} name={item.wheel.name} key={item.wheel.id}>
            {item.change !== "不变" ? <Checkbox className="result-row-progress" checked={appliedDifferenceIds.has(item.wheel.id)} aria-label={`${item.wheel.name}：差异任务${appliedDifferenceIds.has(item.wheel.id) ? "已完成，点击恢复未完成" : "未完成，点击标记已完成"}`} title={appliedDifferenceIds.has(item.wheel.id) ? "恢复为未完成" : "标记为已完成"} onChange={(_event, data) => onSetExecutionState([item.wheel.id], data.checked === true)} /> : <span className="comparison-progress-na" title="方案无变化，无需更改">—</span>}
            <span className={`comparison-change ${item.change}`}>{item.change}</span>
            <span><WheelInfoHover wheel={item.wheel} workbench={workbench}>{item.wheel.name}</WheelInfoHover></span>
            <span>{categoryMap.get(item.wheel.wheelCategoryId)?.name}</span>
            <div className="comparison-tags-column"><TagPills names={catalogLabels.get(item.wheel.id) ?? []} compact /></div>
            <span className="members">{membersByCharacterOrder(item.wheel, workbench.characters).map((member) => characterById.get(member.characterId)?.name).filter(Boolean).join("、")}</span>
            <span className="comparison-stage before">{stage(item.baseline)}</span>
            <span className="comparison-stage after">{stage(item.comparison)}</span>
          </ResultRow>}</VirtualRows>
        </div>
        {!visibleWheels.length && <NoResults label="没有符合条件的方案变化" query={query} onClear={resetFilters} />}
      </section>}
      {comparisonSection === "attributes" && <section className="game-attributes comparison-game-attributes result-section-panel" aria-labelledby="attribute-comparison-title">
        <header className="game-attributes-heading"><h2 id="attribute-comparison-title">属性加成</h2><small>{changedAttributes} 项变化</small></header>
        <AttributeTable groups={attributeGroups}>{item => <ComparisonAttributeRow item={item} name={sourceById.get(item.sourceAttributeId)?.name ?? item.sourceAttributeId} key={item.key} />}</AttributeTable>
      </section>}
    </div>
  );
}

export function ResultView({
  result,
  fragmentEnabled,
  workbench,
  ssrWheelChoices,
  ssrChoiceSuggestions,
  onSetExecutionState,
}: {
  result: SolverResult;
  fragmentEnabled: boolean;
  workbench: Workbench;
  ssrWheelChoices: number;
  ssrChoiceSuggestions: SsrChoiceSuggestion[];
  onSetExecutionState(wheelIds: string[], isApplied: boolean): void;
}): JSX.Element {
  const [resultSection, setResultSection] = useViewState<"wheels" | "attributes" | "fragments">('ResultView.resultSection', "wheels");
  const [ssrDetailsOpen, setSsrDetailsOpen] = useViewState('ResultView.ssrDetailsOpen', false);
  const [query, setQuery] = useViewState('ResultView.query', "");
  const deferredQuery = useDeferredValue(query);
  const [categoryIds, setCategoryIds] = useViewState<string[]>('ResultView.categoryIds', []);
  const [tagIds, setTagIds] = useViewState<string[]>('ResultView.tagIds', []);
  const [partnerIds, setPartnerIds] = useViewState<string[]>('ResultView.partnerIds', []);
  const [partnerGroups, setPartnerGroups] = useViewState<string[]>('ResultView.partnerGroups', []);
  const [rankOrder, setRankOrder] = useViewState<ResultRankOrder>('ResultView.rankOrder', "默认");
  const [progress, setProgress] = useViewState('ResultView.progress', "全部");
  const { sourceById, characterById, categoryMap, wheelById, tags, labels: catalogLabels, partnerGroupById } = catalogIndex(workbench);
  const allWheels = useMemo(() => result.fateWheels.map(entry => ({ entry, wheel: wheelById.get(entry.fateWheelId) }))
    .filter((item): item is { entry: SolverResult["fateWheels"][number]; wheel: Wheel } => Boolean(item.wheel)), [result.fateWheels, wheelById]);
  const appliedWheelCount = allWheels.filter(
    ({ entry }) => entry.isApplied,
  ).length;
  type OmittedResultFacet = "category" | "tag" | "partner" | "quality";
  const searchDocuments = useMemo(() => new Map(allWheels.map(item => {
    const categoryName = categoryMap.get(item.wheel.wheelCategoryId)?.name ?? "";
    const partnerNames = item.wheel.members.map(member => characterById.get(member.characterId)?.name ?? "");
    const labels = catalogLabels.get(item.wheel.id) ?? [];
    const status = item.entry.isApplied ? "已完成" : "未完成";
    return [item.wheel.id, cachedResultDocument(workbench, item.entry, () => prepareSearchDocument({
          text: [item.wheel.name, categoryName, ...labels, ...partnerNames],
          fields: {
            name: item.wheel.name,
            category: categoryName,
            tag: labels,
            element: labels,
            attribute: labels,
            partner: partnerNames,
            rank: item.entry.rank,
            star: item.entry.star,
            progress: status,
          },
          aliases: {
            名称: "name",
            关系命轮: "name",
            命轮: "name",
            类别: "category",
            分类: "category",
            标签: "tag",
            元素: "element",
            属性: "attribute",
            伙伴: "partner",
            阶: "rank",
            星: "star",
            进度: "progress",
            状态: "progress",
          },
    }))];
  })), [allWheels, workbench]);
  const searchMatches = useMemo(() => {
    const matches = compileSearchQuery(deferredQuery);
    return new Set(allWheels.filter(item => matches(searchDocuments.get(item.wheel.id)!)).map(item => item.wheel.id));
  }, [allWheels, searchDocuments, deferredQuery]);
  const matchesResultFilters = (item: (typeof allWheels)[number], omittedFacet?: OmittedResultFacet): boolean =>
    searchMatches.has(item.wheel.id)
    && (omittedFacet === "category" || !categoryIds.length || categoryIds.includes(item.wheel.wheelCategoryId))
    && (omittedFacet === "tag" || !tagIds.length || (tags.get(item.wheel.id) ?? []).some(tagId => tagIds.includes(tagId)))
    && (progress === "全部" || progress === (item.entry.isApplied ? "已完成" : "未完成"))
    && matchesResultMembers(item.wheel, characterById, omittedFacet === "partner" ? [] : partnerIds, omittedFacet === "quality" ? [] : partnerGroups);
  const categoryFacets = collectResultFacetValues(
    allWheels.filter((item) => matchesResultFilters(item, "category")),
    characterById,
    (wheel) => tags.get(wheel.id) ?? [],
  );
  const tagFacets = collectResultFacetValues(
    allWheels.filter((item) => matchesResultFilters(item, "tag")),
    characterById,
    (wheel) => tags.get(wheel.id) ?? [],
  );
  const partnerFacets = collectResultFacetValues(
    allWheels.filter((item) => matchesResultFilters(item, "partner")),
    characterById,
    (wheel) => tags.get(wheel.id) ?? [],
  );
  const qualityFacets = collectResultFacetValues(
    allWheels.filter((item) => matchesResultFilters(item, "quality")),
    characterById,
    (wheel) => tags.get(wheel.id) ?? [],
  );
  const categoryOptions: FilterOption[] = workbench.wheelCategories
    .filter((category) => categoryFacets.categoryIds.has(category.id))
    .map((category) => ({ value: category.id, label: category.name }));
  const resultTagOptions = tagOptions(workbench)
    .slice(1)
    .filter((option) => tagFacets.tagIds.has(option.value));
  const partnerOptions: FilterOption[] = workbench.characters
    .filter((character) => partnerFacets.partnerIds.has(character.id))
    .sort((left, right) => left.order - right.order)
    .map((character) => ({ value: character.id, label: character.name }));
  const qualityOptions = resourceGroupOptions
    .slice(1)
    .filter((option) =>
      qualityFacets.partnerGroups.has(option.value as ResourceGroup),
    );
  const categoryCandidateKey = categoryOptions
    .map((option) => option.value)
    .join("\u0000");
  const tagCandidateKey = resultTagOptions
    .map((option) => option.value)
    .join("\u0000");
  const partnerCandidateKey = partnerOptions
    .map((option) => option.value)
    .join("\u0000");
  const qualityCandidateKey = qualityOptions
    .map((option) => option.value)
    .join("\u0000");
  useEffect(() => {
    const retainAvailable = (
      current: string[],
      available: Set<string>,
    ): string[] => {
      const next = current.filter((value) => available.has(value));
      return next.length === current.length ? current : next;
    };
    setCategoryIds((current) =>
      retainAvailable(current, categoryFacets.categoryIds),
    );
    setTagIds((current) => retainAvailable(current, tagFacets.tagIds));
    setPartnerIds((current) =>
      retainAvailable(current, partnerFacets.partnerIds),
    );
    setPartnerGroups((current) =>
      retainAvailable(current, qualityFacets.partnerGroups),
    );
  }, [
    categoryCandidateKey,
    tagCandidateKey,
    partnerCandidateKey,
    qualityCandidateKey,
  ]);
  const wheels = allWheels
    .filter((item) => matchesResultFilters(item))
    .sort((left, right) => compareResultWheels(left, right, rankOrder));
  const attributes = [...result.finalAttributes].sort(
    (left, right) =>
      (sourceById.get(left.sourceAttributeId)?.order ?? 0) -
        (sourceById.get(right.sourceAttributeId)?.order ?? 0) ||
      left.valueKind.localeCompare(right.valueKind),
  );
  const attributesByGroup = new Map<string, FinalAttributeResult[]>();
  for (const item of attributes) {
    const group = sourceById.get(item.sourceAttributeId)?.group ?? "其他属性";
    attributesByGroup.set(group, [...(attributesByGroup.get(group) ?? []), item]);
  }
  const attributeGroups = [...attributesByGroup.entries()].sort(
    ([, left], [, right]) =>
      (sourceById.get(left[0]?.sourceAttributeId ?? "")?.order ?? 0) -
      (sourceById.get(right[0]?.sourceAttributeId ?? "")?.order ?? 0),
  );
  const resetFilters = (): void => {
    setQuery("");
    setCategoryIds([]);
    setTagIds([]);
    setPartnerIds([]);
    setPartnerGroups([]);
    setRankOrder("默认");
    setProgress("全部");
  };
  const activeFilterGroups = [
    Boolean(query),
    Boolean(categoryIds.length),
    Boolean(tagIds.length),
    Boolean(partnerIds.length),
    Boolean(partnerGroups.length),
    rankOrder !== "默认",
    progress !== "全部",
  ].filter(Boolean).length;
  const visibleIds = wheels.map(({ entry }) => entry.fateWheelId);
  const allVisibleApplied =
    wheels.length > 0 && wheels.every(({ entry }) => entry.isApplied);
  const someVisibleApplied = wheels.some(({ entry }) => entry.isApplied);
  const bulkChecked: boolean | "mixed" = allVisibleApplied
    ? true
    : someVisibleApplied
      ? "mixed"
      : false;
  return (
    <div className="result-view upgrade-view">
      {ssrWheelChoices > 0 && <section className="ssr-allocation" aria-label="SSR 自选分配"><strong>SSR 自选分配</strong><div>{(ssrDetailsOpen?ssrChoiceSuggestions:ssrChoiceSuggestions.slice(0,4)).map(item=><span key={item.characterId}>{characterById.get(item.characterId)?.name ?? item.characterId}<b>× {item.count}</b></span>)}{!ssrChoiceSuggestions.length&&<span>此记录没有分配建议</span>}</div>{ssrChoiceSuggestions.length>4&&<button type="button" onClick={()=>setSsrDetailsOpen(open=>!open)}>{ssrDetailsOpen?'收起':`全部 ${ssrChoiceSuggestions.length} 项`}</button>}</section>}

      <nav className="result-view-switcher" aria-label="结果视图">
        <button type="button" className={resultSection === "wheels" ? "active" : ""} aria-current={resultSection === "wheels" ? "page" : undefined} onClick={() => setResultSection("wheels")}><span>升级清单</span><small>{allWheels.length}</small></button>
        <button type="button" className={resultSection === "attributes" ? "active" : ""} aria-current={resultSection === "attributes" ? "page" : undefined} onClick={() => setResultSection("attributes")}><span>属性收益</span><small>{attributes.length}</small></button>
        <button type="button" className={resultSection === "fragments" ? "active" : ""} aria-current={resultSection === "fragments" ? "page" : undefined} onClick={() => setResultSection("fragments")}><span>碎片规划</span><small>{result.fragmentAnalysis ? result.fragmentAnalysis.shortages.length : 0}</small></button>
      </nav>
      {resultSection === "wheels" && <>
      <div className="result-toolbar">
        <SearchBox
          value={query}
          onChange={setQuery}
          placeholder="筛选关系命轮"
          resultCount={wheels.length}
          totalCount={allWheels.length}
        />
        <span
          className="result-execution-summary"
          aria-label={
            "已完成 " +
            appliedWheelCount +
            "，共 " +
            allWheels.length +
            " 个关系命轮"
          }
        >
          <CheckmarkRegular />
          <span>已完成</span>
          <b>{appliedWheelCount}</b>
          <i>/</i>
          <span>{allWheels.length}</span>
        </span>
        <div className="completion-filter" role="group" aria-label="完成状态">{['全部','未完成','已完成'].map(value=><button type="button" key={value} aria-pressed={progress===value} onClick={()=>setProgress(value)}>{value}</button>)}</div>
        <div className="result-toolbar-actions">
          {activeFilterGroups > 0 && (
            <Button
              appearance="subtle"
              size="small"
              icon={<DismissRegular />}
              onClick={resetFilters}
            >
              清除筛选
            </Button>
          )}
        </div>
      </div>
      <ActiveFilters clear={resetFilters} items={[
        ...facetChips('类别',categoryIds,categoryOptions,setCategoryIds),...facetChips('标签',tagIds,resultTagOptions,setTagIds),...facetChips('伙伴',partnerIds,partnerOptions,setPartnerIds),...facetChips('品质',partnerGroups,qualityOptions,setPartnerGroups),
        ...(query?[{key:'search',label:`搜索：${query}`,remove:()=>setQuery('')}]:[]),...(progress!=='全部'?[{key:'progress',label:`进度：${progress}`,remove:()=>setProgress('全部')}]:[]),...(rankOrder!=='默认'?[{key:'sort',label:`排序：${rankOrder}`,remove:()=>setRankOrder('默认')}]:[])
      ]}/>
      <details className="execution-help"><summary>执行进度 · 当前筛选 {wheels.length} 项可标记</summary><p>勾选仅标记已在游戏内完成，不扣除库存。表头勾选仅影响当前筛选的 {wheels.length} 项。</p></details>
      <div className="data-table result-table upgrade-table" data-scroll-id="result-table">
        <div className="table-row table-head">
          <ResultProgressHeader
            checked={bulkChecked}
            disabled={!wheels.length}
            filter={progress}
            onToggleAll={() =>
              onSetExecutionState(visibleIds, !allVisibleApplied)
            }
            onFilterChange={setProgress}
          />
          <span className="upgrade-name-head">关系命轮</span>
          <ResultTableMultiFilter label="类别" options={categoryOptions} selectedValues={categoryIds} onChange={setCategoryIds} />
          <ResultTableMultiFilter label="标签" options={resultTagOptions} selectedValues={tagIds} onChange={setTagIds} />
          <ResultPartnerFilter
            partnerOptions={partnerOptions}
            qualityOptions={qualityOptions}
            partnerGroupById={partnerGroupById}
            selectedPartnerIds={partnerIds}
            selectedQualityIds={partnerGroups}
            onPartnerChange={setPartnerIds}
            onQualityChange={setPartnerGroups}
          />
          <div className="upgrade-stage-head"><ResultTableSort
            label="目标阶星"
            value={rankOrder}
            onChange={setRankOrder}
          /></div>
          <span className="upgrade-detail-head">详情</span>
        </div>
        <VirtualRows estimate={36} items={wheels} itemKey={item => item.wheel.id}>{({ entry, wheel }) => {
          const partners = membersByCharacterOrder(wheel, workbench.characters).map(member => characterById.get(member.characterId)?.name).filter(Boolean).join('、');
          return (
          <ResultRow id={wheel.id} name={wheel.name} key={wheel.id}>
            <Checkbox
              className="result-row-progress"
              checked={entry.isApplied === true}
              aria-label={
                wheel.name +
                "：" +
                (entry.isApplied
                  ? "已完成，点击恢复未完成"
                  : "未完成，点击标记已完成")
              }
              title={entry.isApplied ? "恢复为未完成" : "标记为已完成"}
              onChange={(_event, data) =>
                onSetExecutionState([entry.fateWheelId], data.checked === true)
              }
            />
            <span className="upgrade-wheel-name" title={wheel.name}>{wheel.name}</span>
            <span className="upgrade-category" title={categoryMap.get(wheel.wheelCategoryId)?.name}>{categoryMap.get(wheel.wheelCategoryId)?.name ?? '未知类别'}</span>
            <div className="upgrade-tags"><TagPills names={catalogLabels.get(wheel.id) ?? []} /></div>
            <span className="upgrade-partners" title={partners}>{partners || '—'}</span>
            <span className="upgrade-stage">
              {wheelStageLabel(
                entry.rank,
                entry.star,
                workbench.globalSettings.maxRank,
              )}
            </span>
            <span className="upgrade-detail"><WheelInfoHover wheel={wheel} workbench={workbench}>详情</WheelInfoHover></span>
          </ResultRow>
        ); }}</VirtualRows>
      </div>
      <p className="upgrade-note">目标阶星是计算出的配置；勾选仅记录你在游戏内的完成情况，不会操作游戏。</p>
      {!wheels.length && (
        <NoResults
          label="没有符合条件的求解结果"
          query={query}
          onClear={resetFilters}
        />
      )}
      </>}
      {resultSection === "attributes" && <section className="game-attributes result-section-panel" aria-labelledby="result-attribute-title">
        <h2 id="result-attribute-title">属性加成</h2>
        <AttributeTable groups={attributeGroups}>{item => <AttributeRow item={item} name={sourceById.get(item.sourceAttributeId)?.name ?? item.sourceAttributeId} key={`${item.sourceAttributeId}-${item.valueKind}`} />}</AttributeTable>
      </section>}
      {resultSection === 'fragments' && !result.fragmentAnalysis && <EmptyState label={fragmentEnabled?'碎片规划尚未完成':'本次计算未开启碎片分析'} detail={fragmentEnabled?'可重新计算以获取完整规划。':'在方案的计算选项中开启碎片分析后，重新计算。'}/>}
      {resultSection === "fragments" && result.fragmentAnalysis && (
        <FragmentAnalysisView
          analysis={result.fragmentAnalysis}
          workbench={workbench}
        />
      )}
    </div>
  );
}

export function AttributeRow({
  item,
  name,
}: {
  item: FinalAttributeResult;
  name: string;
}): JSX.Element {
  return (
    <div className="attribute-row">
      <span>{name}</span>
      <strong>
        +{formatNumber(item.value, 6)}
        {item.valueKind === "percent" ? "%" : ""}
      </strong>
    </div>
  );
}

export function ComparisonAttributeRow({
  item,
  name,
}: {
  item: ReturnType<typeof compareResultAttributes>[number];
  name: string;
}): JSX.Element {
  const unit = item.valueKind === "percent" ? "%" : "";
  const changed = Math.abs(item.delta) > 1e-9;
  return (
    <div className={`attribute-row comparison-attribute-row ${changed ? "changed" : ""}`}>
      <span>{name}</span>
      <span className="comparison-attribute-values">
        <small>{formatNumber(item.before, 6)}{unit} → {formatNumber(item.after, 6)}{unit}</small>
        <strong className={item.delta > 0 ? "positive" : item.delta < 0 ? "negative" : ""}>
          {item.delta > 0 ? "+" : ""}{formatNumber(item.delta, 6)}{unit}
        </strong>
      </span>
    </div>
  );
}

export function WheelInfoHover({
  wheel,
  workbench,
  children,
}: {
  wheel: Wheel | undefined;
  workbench: Workbench;
  children: ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  if (!wheel) return <span>{children}</span>;
  const category = workbench.wheelCategories.find(
    (item) => item.id === wheel.wheelCategoryId,
  );
  const characterById = new Map(
    workbench.characters.map((character) => [character.id, character]),
  );
  const sourceById = new Map(
    workbench.sourceAttributes.map((source) => [source.id, source]),
  );
  const gainLabel = (gain: AttributeGain): string =>
    `${sourceById.get(gain.sourceAttributeId)?.name ?? gain.sourceAttributeId} ${formatNumber(gain.value, 6)}${gain.valueKind === "percent" ? "%" : ""}`;
  const partners = membersByCharacterOrder(wheel, workbench.characters)
    .map(
      (member) =>
        characterById.get(member.characterId)?.name ?? member.characterId,
    )
    .join("、");
  const content = (
    <div className="wheel-hover-card">
      <strong>{wheel.name}</strong>
      <dl>
        <div>
          <dt>类别</dt>
          <dd>{category?.name ?? "未知类别"}</dd>
        </div>
        <div>
          <dt>星级</dt>
          <dd className="wheel-hover-stars">
            {"☆".repeat(Math.max(1, wheel.maxStar))}
          </dd>
        </div>
        <div className="wheel-hover-wide"><dt>标签</dt><dd>{tagNames(workbench,wheel).join("、") || "—"}</dd></div>
        <div className="wheel-hover-wide">
          <dt>伙伴</dt>
          <dd>{partners || "—"}</dd>
        </div>
        <div className="wheel-hover-wide">
          <dt>收益/星</dt>
          <dd>{wheel.perStarGains.map(gainLabel).join(" · ") || "—"}</dd>
        </div>
        <div className="wheel-hover-wide">
          <dt>收益/阶</dt>
          <dd>{wheel.perRankGains.map(gainLabel).join(" · ") || "—"}</dd>
        </div>
      </dl>
    </div>
  );
  return (
    <Popover open={open} onOpenChange={(_event, data) => setOpen(data.open)} positioning="below-start">
      <PopoverTrigger disableButtonEnhancement>
      <button type="button" className="wheel-hover-trigger" aria-expanded={open} aria-label={`查看${wheel.name}详情`}>
        {children}
      </button>
      </PopoverTrigger>
      <PopoverSurface className="wheel-detail-surface" aria-label="命轮详情">{content}</PopoverSurface>
    </Popover>
  );
}

export function FragmentAnalysisView({
  analysis,
  workbench,
}: {
  analysis: NonNullable<SolverResult["fragmentAnalysis"]>;
  workbench: Workbench;
}): JSX.Element {
  const { characterById, wheelById, sourceById } = catalogIndex(workbench);
  const [selectedCharacterId, setSelectedCharacterId] = useViewState<string | undefined>('FragmentAnalysisView.character', undefined);
  const selectedShortage = analysis.shortages.find(item => item.characterId === selectedCharacterId);
  const visibleChanges = selectedShortage ? analysis.wheelChanges.filter(item => selectedShortage.affectedFateWheelIds.includes(item.fateWheelId)) : analysis.wheelChanges;
  const totalShortage = analysis.shortages.reduce(
    (total, item) => total + item.shortage,
    0,
  );
  const objectiveImprovementPercent =
    analysis.objectiveImprovement === undefined ||
    analysis.baselineObjectiveScore === undefined ||
    Math.abs(analysis.baselineObjectiveScore) < 1e-9
      ? undefined
      : (analysis.objectiveImprovement /
          Math.abs(analysis.baselineObjectiveScore)) *
        100;
  const successful =
    analysis.status === "completed" || analysis.status === "estimated";
  const stage = (rank: number, star: number): string =>
    rank || star
      ? wheelStageLabel(rank, star, workbench.globalSettings.maxRank)
      : "未选择";
  const signed = (value: number): string =>
    `${value > 0 ? "+" : ""}${formatNumber(value, 6)}`;
  const percent = (value: number): string => `${formatNumber(value, 2)}%`;
  const changeKind = (
    item: NonNullable<SolverResult["fragmentAnalysis"]>["wheelChanges"][number],
  ): string => {
    const fromSelected = item.fromRank > 0 || item.fromStar > 0;
    const toSelected = item.toRank > 0 || item.toStar > 0;
    if (!fromSelected && toSelected) return "新增";
    if (fromSelected && !toSelected) return "退出";
    return item.contributionDelta >= 0 ? "提升" : "替换";
  };
  if (!successful)
    return (
      <section
        className={`fragment-analysis fragment-analysis-${analysis.status}`}
        aria-labelledby="fragment-analysis-title"
      >
        <div
          className="fragment-analysis-state"
          role={analysis.status === "failed" ? "alert" : "status"}
        >
          <div>
            <h2 id="fragment-analysis-title">碎片分析</h2>
            <p>
              {analysis.status === "cancelled"
                ? "碎片缺口分析已取消，当前求解结果仍然可用。"
                : "碎片缺口分析未完成，当前求解结果仍然可用。"}
            </p>
          </div>
          {analysis.message && <span>{analysis.message}</span>}
        </div>
      </section>
    );
  return (
    <section
      className="fragment-analysis" data-scroll-id="fragment-analysis"
      aria-labelledby="fragment-analysis-title"
    >
      <header className="fragment-analysis-summary">
        <div className="fragment-analysis-heading">
          <div>
            <h2 id="fragment-analysis-title">碎片分析</h2>
            <p>仅分析当前未忽略的碎片约束</p>
          </div>
          <span className={`analysis-status ${analysis.status}`}>
            {analysis.status === "completed" ? "精确" : "估算"}
          </span>
        </div>
        <dl className="fragment-analysis-metrics">
          <div>
            <dt>理想方案评分</dt>
            <dd
              className={
                (analysis.relaxedObjectiveScore ?? 0) > 0 ? "positive" : ""
              }
            >
              {analysis.relaxedObjectiveScore === undefined
                ? "—"
                : formatNumber(analysis.relaxedObjectiveScore, 2)}
            </dd>
          </div>
          <div>
            <dt>综合提升</dt>
            <dd
              className={
                (objectiveImprovementPercent ?? 0) > 0 ? "positive" : ""
              }
            >
              {objectiveImprovementPercent === undefined
                ? "—"
                : `${objectiveImprovementPercent > 0 ? "+" : ""}${formatNumber(objectiveImprovementPercent, 2)}%`}
            </dd>
          </div>
          <div>
            <dt>理想方案对比</dt>
            <dd>
              {analysis.wheelChanges.length}
              <small>盘</small>
            </dd>
          </div>
          <div>
            <dt>碎片缺口</dt>
            <dd>
              {analysis.shortages.length}
              <small>位</small>
              <i>·</i>
              {totalShortage}
              <small>片</small>
            </dd>
          </div>
        </dl>
      </header>
      {analysis.status === "estimated" && (
        <div className="fragment-analysis-note" role="status">
          基准或放宽方案为限时可行解，贡献顺序可能随更优解变化。
        </div>
      )}
      {analysis.message && (
        <div className="fragment-analysis-note neutral" role="status">
          {analysis.message}
        </div>
      )}
      <div className="fragment-comparison-grid">
        <section
          className="fragment-table-panel"
          aria-labelledby="fragment-shortage-title"
        >
          <header>
            <h3 id="fragment-shortage-title">碎片缺口</h3>

          </header>
          <div className="fragment-table-scroll" data-scroll-id="fragment-table">
            <table className="fragment-change-table fragment-shortage-table">
              <caption className="sr-only">
                受约束伙伴的碎片缺口及影响方案
              </caption>
              <thead>
                <tr>
                  <th scope="col">命轮</th>
                  <th scope="col">碎片缺口</th>
                  <th scope="col">影响方案</th>
                  <th scope="col" className="numeric">
                    每片贡献
                  </th>
                </tr>
              </thead>
              <tbody>
                {analysis.shortages.map((item) => (
                  <tr key={item.characterId} className={selectedCharacterId === item.characterId ? 'selected' : ''}>
                    <td>
                      <button type="button" className="fragment-character-name" aria-pressed={selectedCharacterId === item.characterId} onClick={() => setSelectedCharacterId(current => current === item.characterId ? undefined : item.characterId)}>
                        {characterById.get(item.characterId)?.name ??
                          item.characterId}
                      </button>
                    </td>
                    <td>
                      <div className="fragment-shortage-value">
                        <strong>缺 {item.shortage}</strong>
                        <span>
                          已有 {item.available} · 需求 {item.required}
                        </span>
                      </div>
                    </td>
                    <td>
                      <FragmentDetails id={`shortage:${item.characterId}`} label={`影响方案（${item.affectedFateWheelIds.length}）`}>
                      <div className="fragment-affected-plans">
                        {item.affectedFateWheelIds.map((id) => (
                          <WheelInfoHover
                            key={id}
                            wheel={wheelById.get(id)}
                            workbench={workbench}
                          >
                            {wheelById.get(id)?.name ?? id}
                          </WheelInfoHover>
                        ))}
                        {!item.affectedFateWheelIds.length && (
                          <span className="unchanged">无直接变化</span>
                        )}
                      </div>
                      </FragmentDetails>
                    </td>
                    <td
                      className={`numeric contribution ${item.contributionPercent > 0 ? "positive" : ""}`}
                    >
                      {formatNumber(
                        item.shortage > 0
                          ? item.contributionPercent / item.shortage * 100
                          : 0,
                        2,
                      )}
                      <small className="contribution-unit">‱/个</small>
                    </td>
                  </tr>
                ))}
                {!analysis.shortages.length && (
                  <tr className="fragment-empty-row">
                    <td colSpan={4}>当前受约束品质没有碎片缺口</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        <section
          className="fragment-table-panel"
          aria-labelledby="fragment-plan-title"
        >
          <header>
            <h3 id="fragment-plan-title">理想方案对比</h3>
            {selectedShortage && <button className="fragment-clear" type="button" onClick={() => setSelectedCharacterId(undefined)}>{characterById.get(selectedCharacterId!)?.name} · 清除筛选</button>}
          </header>
          <div className="fragment-table-scroll" data-scroll-id="fragment-changes">
            <table className="fragment-change-table fragment-plan-table">
              <caption className="sr-only">
                放宽碎片约束后的理想关系命轮方案
              </caption>
              <thead>
                <tr>
                  <th scope="col">关系命轮</th>
                  <th scope="col">阶星变化</th>
                  <th scope="col">收益</th>
                  <th scope="col" className="numeric">
                    贡献
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleChanges.map((item) => {
                  const wheel = wheelById.get(item.fateWheelId);
                  return (
                    <tr key={item.fateWheelId}>
                      <td>
                        <div className="fragment-wheel-name">
                          <strong>
                            <WheelInfoHover wheel={wheel} workbench={workbench}>
                              {wheel?.name ?? item.fateWheelId}
                            </WheelInfoHover>
                          </strong>
                        </div>
                      </td>
                      <td>
                        <div className="fragment-stage-change">
                          <span className={`change-kind ${changeKind(item)}`}>
                            {changeKind(item)}
                          </span>
                          <span className="fragment-stage-flow">
                            <span>{stage(item.fromRank, item.fromStar)}</span>
                            <ArrowRightRegular aria-hidden />
                            <strong>{stage(item.toRank, item.toStar)}</strong>
                          </span>
                        </div>
                      </td>
                      <td>
                        <FragmentDetails id={`change:${item.fateWheelId}`} label="属性收益">
                        <div className="fragment-gains">
                          {item.gains.map((gain) => (
                            <span
                              className={
                                gain.value > 0 ? "positive" : "negative"
                              }
                              key={`${gain.sourceAttributeId}-${gain.valueKind}`}
                            >
                              <b>
                                {sourceById.get(gain.sourceAttributeId)?.name ??
                                  gain.sourceAttributeId}
                              </b>
                              {signed(gain.value)}
                              {gain.valueKind === "percent" ? "%" : ""}
                            </span>
                          ))}
                          {!item.gains.length && (
                            <span className="unchanged">无属性变化</span>
                          )}
                        </div>
                        </FragmentDetails>
                      </td>
                      <td
                        className={`numeric contribution ${item.contributionPercent > 0 ? "positive" : item.contributionPercent < 0 ? "negative" : ""}`}
                      >
                        {percent(item.contributionPercent)}
                      </td>
                    </tr>
                  );
                })}
                {!visibleChanges.length && (
                  <tr className="fragment-empty-row">
                    <td colSpan={4}>{selectedShortage ? "该缺口没有直接关系变化" : "当前碎片约束没有造成方案差异"}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </section>
  );
}


function FragmentDetails({ id, label, children }: { id:string; label: string; children: ReactNode }) {
  const [open, setOpen] = useViewState(`fragment.${id}.expanded`, false);
  return <details open={open} onToggle={event => setOpen(event.currentTarget.open)} className="fragment-mobile-details"><summary>{label}<ChevronDownRegular aria-hidden /></summary>{children}</details>;
}

function ResultRow({ id, name, children }: { id:string; name: string; children: ReactNode }) {
  const [expanded, setExpanded] = useViewState(`row.${id}.expanded`, false);
  return <div className={`table-row result-data-row ${expanded ? 'row-expanded' : ''}`}>{children}
    <button className="result-row-disclosure" type="button" aria-label={`${expanded ? '收起' : '展开'}${name}详情`} aria-expanded={expanded} onClick={() => setExpanded(value => !value)}><ChevronDownRegular /></button>
  </div>;
}
